import { useEffect, useRef, useState } from "react";

/**
 * Extracts the numeric value from a formatted string like "₹1,163.98 Cr" or "68.1%" or plain numbers.
 * Returns { prefix, number, suffix } or null if no number found.
 */
function parseFormattedValue(val) {
  if (val === null || val === undefined || val === "—" || val === "N/A") return null;
  if (typeof val === "number") return { prefix: "", number: val, suffix: "" };
  const str = String(val);
  // Match: optional prefix (₹, etc), number with commas/decimals, optional suffix (Cr, %, etc)
  const match = str.match(/^([^0-9]*?)([\d,]+\.?\d*)\s*(.*)$/);
  if (!match) return null;
  const num = parseFloat(match[2].replace(/,/g, ""));
  if (isNaN(num)) return null;
  return { prefix: match[1], number: num, suffix: match[3] ? " " + match[3].trim() : "" };
}

/**
 * Formats a number back to Indian locale with the same decimal places as the original.
 */
function formatAnimatedNumber(num, decimals) {
  if (decimals === 0) return Math.round(num).toLocaleString("en-IN");
  return num.toLocaleString("en-IN", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

/**
 * AnimatedValue — renders a value with a smooth count-up/down animation when it changes.
 * Works with formatted strings like "₹1,163.98 Cr", "68.1%", or plain numbers.
 * 
 * @param {string|number} value - The value to display and animate
 * @param {number} duration - Animation duration in ms (default 800)
 * @param {string} className - Additional CSS classes for the wrapper
 */
export default function AnimatedValue({ value, duration = 800, className = "" }) {
  const [display, setDisplay] = useState(value);
  const animRef = useRef(null);
  const prevParsed = useRef(null);

  useEffect(() => {
    const parsed = parseFormattedValue(value);
    const prev = prevParsed.current;

    // If can't parse or first render, just set directly
    if (!parsed || !prev) {
      prevParsed.current = parsed;
      setDisplay(value);
      return;
    }

    // If prefix/suffix changed (different format), set directly
    if (prev.prefix !== parsed.prefix || prev.suffix !== parsed.suffix) {
      prevParsed.current = parsed;
      setDisplay(value);
      return;
    }

    // Same number, no animation needed
    if (prev.number === parsed.number) return;

    // Animate from prev.number to parsed.number
    const startNum = prev.number;
    const endNum = parsed.number;
    const decimals = String(parsed.number).includes(".") ? (String(parsed.number).split(".")[1] || "").length : 0;
    const startTime = performance.now();

    // Cancel any running animation
    if (animRef.current) cancelAnimationFrame(animRef.current);

    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic for smooth deceleration
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = startNum + (endNum - startNum) * eased;
      const formatted = `${parsed.prefix}${formatAnimatedNumber(current, decimals)}${parsed.suffix}`;
      setDisplay(formatted);

      if (progress < 1) {
        animRef.current = requestAnimationFrame(animate);
      } else {
        // Ensure final value matches exactly
        setDisplay(value);
        prevParsed.current = parsed;
      }
    };

    animRef.current = requestAnimationFrame(animate);
    prevParsed.current = parsed;

    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [value, duration]);

  return <span className={className}>{display}</span>;
}
